/**
 * Types + fetchery dla pluginu `c_private_messages` rozszerzającego shell
 * `c_user_accounts`. Backend endpointy:
 *   - GET /IRESTAPI/v1/C_PRIVATE_MESSAGES_CONTROLLER/inbox?page=N&per_page=N
 *   - GET /IRESTAPI/v1/C_PRIVATE_MESSAGES_CONTROLLER/sent?page=N&per_page=N
 *
 * Frontend route handlers proxy w app/api/iiapi/c-private-messages/{inbox,sent}/.
 */

export type PrivateMessage = {
  id: number;
  title: string;
  text_preview: string;
  sender_login: string;
  sent_date: string;
  /** `null` dla wysłanych (count_view dotyczy recipient). `true`/`false` dla odebranych. */
  is_read: boolean | null;
};

export type PrivateMessagesPagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

export type PrivateMessagesListResponse = {
  ok: true;
  data: {
    messages: PrivateMessage[];
    pagination: PrivateMessagesPagination;
  };
};

/**
 * Fetcha listę wiadomości (inbox lub sent) z route handler proxy.
 *
 * @param folder "inbox" | "sent"
 * @param page Page number (1-based, default 1)
 * @param perPage Items per page (default 20, max 100)
 */
export async function fetchPrivateMessages(
  folder: "inbox" | "sent",
  page: number = 1,
  perPage: number = 20,
): Promise<{
  messages: PrivateMessage[];
  pagination: PrivateMessagesPagination;
}> {
  const qs = `?page=${page}&per_page=${perPage}`;
  const res = await fetch(`/api/iiapi/c-private-messages/${folder}${qs}`, {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Fetch ${folder} failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | PrivateMessagesListResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body
        ? body.message ?? "Fetch failed"
        : "Invalid response shape",
    );
  }
  return {
    messages: body.data?.messages ?? [],
    pagination: body.data?.pagination ?? {
      page: 1,
      per_page: perPage,
      total: 0,
      total_pages: 0,
    },
  };
}

export type SendMessagePayload = {
  to_login: string;
  title: string;
  text: string;
};

export type SendMessageResponse =
  | { ok: true; data: { success: true; recipient_login: string } }
  | { ok: false; error?: string; message?: string; code?: number | string };

/**
 * Wysyła nową wiadomość przez route handler proxy.
 *
 * @returns `{success: true}` lub rzuca Error z message z backendu.
 */
export async function sendPrivateMessage(payload: SendMessagePayload): Promise<{ success: true }> {
  const res = await fetch("/api/iiapi/c-private-messages/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  const body = (await res.json()) as SendMessageResponse;
  if (!res.ok || !("ok" in body) || body.ok !== true) {
    const errMsg = "message" in body && body.message
      ? body.message
      : `Send failed (HTTP ${res.status})`;
    throw new Error(errMsg);
  }
  return { success: true };
}
